/*
|--------------------------------------------------------------------------
| Component: MediaUploader.jsx
| Author: Sayed Ahmed
| Description: A file uploader component that supports drag-and-drop and
|              previews. Tracks upload progress and supports cancellation.
|--------------------------------------------------------------------------
*/

import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDropzone } from "react-dropzone";
import { useDispatch } from "react-redux";
import { uploadMedia } from "../../../store/custom-actions/mediaSlice";
import "../../../styles/media.css";

/**
 * @param {Object} props
 * @param {'image'|'video'} [props.mediaType='image'] - نوع الميديا: صورة أو فيديو (يُحدد عند وضع الزر)
 */
export default function MediaUploader({ onUploaded, mediaType = "image" }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [uploads, setUploads] = useState([]);

  // Handle file drop event
  const onDrop = useCallback(
    (acceptedFiles) => {
      if (acceptedFiles.length === 0) return;

      acceptedFiles.forEach((file) => {
        const preview = URL.createObjectURL(file);
        const controller = new AbortController();

        const newUpload = {
          file,
          preview,
          progress: 0,
          controller,
        };

        // Add file to local state
        setUploads((prev) => [...prev, newUpload]);

        // Dispatch upload action
        dispatch(
          uploadMedia({
            file,
            controller,
            onProgress: (percent) => {
              setUploads((prev) =>
                prev.map((u) =>
                  u.file === file ? { ...u, progress: percent } : u
                )
              );
            },
          })
        ).then((result) => {
          // Notify parent if successful
          if (
            typeof onUploaded === "function" &&
            result.meta.requestStatus === "fulfilled"
          ) {
            onUploaded();
          }

          // Remove file from local state after upload
          setUploads((prev) => prev.filter((u) => u.file !== file));
        });
      });
    },
    [dispatch, onUploaded]
  );

  // Setup dropzone: image or video based on mediaType
  const dropzoneAccept =
    mediaType === "video" ? { "video/*": [] } : { "image/*": [] };
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: dropzoneAccept,
    multiple: true,
  });

  // Cancel an ongoing upload
  const cancelUpload = (file) => {
    const upload = uploads.find((u) => u.file === file);
    if (upload?.controller) {
      upload.controller.abort();
    }
    setUploads((prev) => prev.filter((u) => u.file !== file));
  };

  return (
    <>
      {/* Dropzone area */}
      <div
        {...getRootProps()}
        className="dropzone-area border border-dashed p-4 rounded text-center mb-4 bg-white"
        style={{ cursor: "pointer" }}
      >
        <input {...getInputProps()} />
        {isDragActive ? (
          <p className="text-primary fw-bold mb-0">
            {mediaType === "video"
              ? t("Drop the videos here") + " ..."
              : t("Drop the images here") + " ..."}
          </p>
        ) : (
          <p className="text-muted mb-0">
            <i className="bi bi-upload fs-3 d-block mb-2" />
            {mediaType === "video"
              ? t("Drag & drop videos here, or click to select")
              : t("Drag & drop images here, or click to select")}
          </p>
        )}
      </div>

      {/* Preview uploads with progress */}
      {uploads.length > 0 && (
        <div className="row">
          {uploads.map((upload, index) => {
            const isVideo = upload.file?.type?.startsWith?.("video/");
            return (
              <div className="col-md-3 mb-4" key={index}>
                <div className="position-relative border rounded shadow-sm overflow-hidden">
                  {/* Image or video preview */}
                  {isVideo ? (
                    <video
                      src={upload.preview}
                      className="img-fluid w-100"
                      style={{ height: 160, objectFit: "cover" }}
                      muted
                      preload="metadata"
                    />
                  ) : (
                    <img
                      src={upload.preview}
                      alt="preview"
                      className="img-fluid w-100"
                      style={{ height: 160, objectFit: "cover" }}
                    />
                  )}

                  {/* Cancel button */}
                  <button
                    className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1 rounded-circle d-flex align-items-center justify-content-center shadow"
                    style={{ width: 28, height: 28 }}
                    onClick={() => cancelUpload(upload.file)}
                  >
                    <i className="fas fa-times"></i>
                  </button>

                  {/* Progress bar */}
                  <div className="progress rounded-0" style={{ height: 6 }}>
                    <div
                      className="progress-bar bg-success"
                      role="progressbar"
                      style={{ width: `${upload.progress}%` }}
                      aria-valuenow={upload.progress}
                      aria-valuemin="0"
                      aria-valuemax="100"
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
